import { init } from "@interfere/vite/init";
import { InterfereProvider } from "@interfere/vite/provider";
import { traceMeta } from "@interfere/vite/tanstack-start";
import {
  HeadContent,
  Scripts,
  createRootRoute,
  useRouter,
} from "@tanstack/react-router";
import { Mail, RotateCcw } from "lucide-react";
import { useEffect } from "react";
import { Toaster } from "react-hot-toast";

import { GlobalPlayerBar } from "@/components/global-player-bar";
import { Link } from "@/components/link";
import { AudioContextProvider } from "@/components/providers/audio-context-provider";
import { PlayerProvider } from "@/components/providers/player-provider";
import { Button } from "@/components/ui/button";
import { startAnalytics } from "@/lib/analytics";
import { SUPPORT_EMAIL } from "@/lib/support";

import appCss from "../styles.css?url";

init();

const SITE_ORIGIN = "https://oscar-radio.org";

const supportMailto = (error?: Error) => {
  const subject = "OSCAR Radio: something went wrong";
  const body = [
    "Hi, I hit an error on the OSCAR Radio site.",
    "",
    `Page: ${typeof window === "undefined" ? "" : window.location.href}`,
    error ? `Error: ${error.message}` : "",
  ].join("\n");
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const RootError = ({ error, reset }: { error: Error; reset: () => void }) => {
  const router = useRouter();

  return (
    <div className="flex min-h-[100dvh] w-full flex-col items-center justify-center gap-6 px-6 text-center">
      <div className="flex flex-col gap-3">
        <h1 className="text-5xl font-bold tracking-tighter text-balance sm:text-7xl">
          Something went wrong
        </h1>
        <p className="text-lg text-muted-foreground">
          The page hit an error and could not load.
          <br />
          Trying again usually fixes it. If not, get in touch.
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button
          onClick={() => {
            reset();
            router.invalidate();
          }}
          size="sm"
        >
          <RotateCcw data-icon="inline-start" /> Try again
        </Button>
        <a href={supportMailto(error)}>
          <Button size="sm" variant="outline">
            <Mail data-icon="inline-start" /> Contact support
          </Button>
        </a>
      </div>
    </div>
  );
};

const RootNotFound = () => (
  <div className="flex min-h-[100dvh] w-full flex-col items-center justify-center gap-6 px-6 text-center">
    <div className="flex flex-col gap-3">
      <h1 className="text-5xl font-bold tracking-tighter text-balance sm:text-7xl">
        This page doesn&apos;t exist
      </h1>
      <p className="text-lg text-muted-foreground">
        It might have been moved or renamed.
        <br />
        Head back home, or get in touch if you think this is a mistake.
      </p>
    </div>
    <div className="flex flex-wrap items-center justify-center gap-2">
      <Link href="/">
        <Button size="sm">Back home</Button>
      </Link>
      <a href={supportMailto()}>
        <Button size="sm" variant="outline">
          <Mail data-icon="inline-start" /> Contact support
        </Button>
      </a>
    </div>
  </div>
);

const Analytics = () => {
  useEffect(() => {
    startAnalytics();
  }, []);
  return null;
};

const RootDocument = ({ children }: { children: React.ReactNode }) => (
  <html lang="en">
    {/* oxlint-disable-next-line next/no-head-element */}
    <head>
      <HeadContent />
      <script
        // oxlint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `if(window.matchMedia('(prefers-color-scheme: dark)').matches){document.documentElement.classList.add('dark')}`,
        }}
      />
    </head>
    <body>
      <InterfereProvider>
        <AudioContextProvider>
          <PlayerProvider>
            {children}
            <GlobalPlayerBar />
          </PlayerProvider>
          <Toaster />
          <Analytics />
        </AudioContextProvider>
      </InterfereProvider>
      <Scripts />
    </body>
  </html>
);

export const Route = createRootRoute({
  errorComponent: RootError,

  head: () => ({
    links: [
      {
        href: appCss,
        rel: "stylesheet",
      },
      {
        href: "/icon.png",
        rel: "icon",
      },
    ],
    meta: [
      ...traceMeta(),
      {
        charSet: "utf-8",
      },
      {
        content: "width=device-width, initial-scale=1",
        name: "viewport",
      },
      {
        title: "OSCAR Radio",
      },
      {
        content:
          "OSCAR Radio is a student-run school radio station established in Oundle School in Northamptonshire, England.",
        name: "description",
      },
      { content: "OSCAR Radio", property: "og:site_name" },
      { content: "OSCAR Radio", property: "og:title" },
      {
        content: "Student radio from Oundle School.",
        property: "og:description",
      },
      { content: "website", property: "og:type" },
      { content: SITE_ORIGIN, property: "og:url" },
      { content: `${SITE_ORIGIN}/og.png`, property: "og:image" },
      { content: "1200", property: "og:image:width" },
      { content: "630", property: "og:image:height" },
      {
        content: "OSCAR Radio. Student radio from Oundle School.",
        property: "og:image:alt",
      },
      { content: "summary_large_image", name: "twitter:card" },
    ],
  }),

  notFoundComponent: RootNotFound,

  shellComponent: RootDocument,
});
